/**
 * Configuração e registro de rotas
 */

import { router } from '../utils/router.util';
import { authService } from '../services/auth.service';
import { ROUTES } from '../constants/routes.constants';
import { STORAGE_KEYS } from '../constants/storage.constants';
import { HomePage } from '../pages/home.page';
import { SplashPage } from '../pages/splash.page';
import { LoginPage } from '../pages/login.page';
import { ExternalRedirectCampaignDetailPage } from '../pages/external-redirect-campaign-detail.page';
import { MediaCampaignDetailPage } from '../pages/media-campaign-detail.page';
import { ArtemisRewardPage } from '../pages/artemis-reward.page';
import { HowItWorksPage } from '../pages/how-it-works.page';
import { CarrotRedirectPage } from '../pages/carrot-redirect.page';
import { campaignCache, getCampaignDetailPageType } from '../utils/campaign.util';
import { requireAuth } from '../routes/route-guards';
import { logger } from '../utils/logger.util';

const app = document.getElementById('app');

if (!app) {
  throw new Error('Elemento #app não encontrado');
}

let currentHomePage: HomePage | null = null;

/**
 * Configura todas as rotas da aplicação
 */
export function setupRouter(): void {
  logger.debug('[Router Setup] Registrando rotas...');

  // Rota: /splash
  router.register(ROUTES.SPLASH, () => {
    logger.debug('[Router] Handler /splash executado');
    const splashPage = new SplashPage(app!);
    splashPage.render().catch((error) => {
      logger.error('[Router] Erro ao renderizar splash page:', error);
    });
  });

  // Rota: / (raiz)
  router.register('/', () => {
    logger.debug('[Router] Handler / executado');
    const isAuth = authService.isAuthenticated();
    if (isAuth) {
      logger.debug('[Router] Redirecionando para /home');
      router.navigate(ROUTES.HOME);
    } else {
      logger.debug('[Router] Redirecionando para /login');
      router.navigate(ROUTES.LOGIN);
    }
  });

  // Rota: /login
  router.register(ROUTES.LOGIN, () => {
    logger.debug('[Router] Handler /login executado');
    const loginPage = new LoginPage(app!);
    loginPage.render();
  });

  // Rota: /home
  router.register(ROUTES.HOME, () => {
    logger.debug('[Router] Handler /home executado');
    if (!requireAuth()) return;
    
    // Reutilizar instância se já existe
    if (!currentHomePage) {
      logger.debug('[Router] Criando nova instância de HomePage');
      currentHomePage = new HomePage(app!);
    } else {
      logger.debug('[Router] Reutilizando instância existente de HomePage');
    }
    
    currentHomePage.render();
  });

  // Rota: /campaign/:id
  router.register('/campaign/:id', async () => {
    if (!requireAuth()) return;
    
    // Extrair ID da URL
    const hash = window.location.hash.slice(1);
    const match = hash.match(/\/campaign\/([^/]+)/);
    const campaignId = match ? decodeURIComponent(match[1]) : null;
    
    if (!campaignId) {
      router.navigate(ROUTES.HOME);
      return;
    }
    
    // Buscar campanha do cache para determinar o tipo
    const campaign = campaignCache.getCampaign(campaignId);
    const pageType = getCampaignDetailPageType(campaign);
    
    // Escolher a página baseado no tipo de mídia
    if (pageType === 'external_redirect') {
      const detailPage = new ExternalRedirectCampaignDetailPage(app!);
      await detailPage.render(campaignId);
    } else if (pageType === 'media') {
      const detailPage = new MediaCampaignDetailPage(app!);
      await detailPage.render(campaignId);
    } else {
      // Fallback: tentar buscar da API se não estiver no cache
      logger.warnWithContext('Router', 'Campanha não encontrada no cache, tentando buscar da API...');
      const detailPage = new MediaCampaignDetailPage(app!);
      await detailPage.render(campaignId);
    }
  });

  // Rota: /reward
  router.register(ROUTES.REWARD, () => {
    if (!requireAuth()) return;
    
    const rewardPage = new ArtemisRewardPage(app!);
    rewardPage.render();
  });

  // Rota: /how-it-works
  router.register(ROUTES.HOW_IT_WORKS, () => {
    if (!requireAuth()) return;
    
    const howItWorksPage = new HowItWorksPage(app!);
    howItWorksPage.render();
  });

  // Rota: /redirect — página de redirecionamento (acessível direto, sem splash, sem requireAuth)
  router.register(ROUTES.REDIRECT, () => {
    logger.debug('[Router] Handler /redirect executado');
    const redirectPage = new CarrotRedirectPage(app!);
    redirectPage.render().catch((err) => logger.error('[Router] Erro ao renderizar redirect page:', err));
  });
}

/**
 * Inicializa o roteamento da aplicação.
 * Sempre começa pela splash, exceto quando a URL é /redirect (acesso direto).
 */
export function initializeRouter(): void {
  const initialHash = window.location.hash.slice(1) || '/';
  const routePath = initialHash.indexOf('?') >= 0 ? initialHash.substring(0, initialHash.indexOf('?')) : initialHash;

  if (routePath === ROUTES.REDIRECT) {
    logger.debug('[Router Setup] Acesso direto a /redirect, não redirecionando para splash');
    return;
  }

  // Salvar rota de destino se houver uma diferente de /splash
  if (initialHash !== ROUTES.SPLASH) {
    sessionStorage.setItem(STORAGE_KEYS.PENDING_ROUTE, initialHash);
    logger.debug('[Router Setup] Rota pendente salva:', initialHash);
  }

  // Sempre começar pela splash screen
  logger.debug('[Router Setup] Inicializando roteamento. Hash inicial:', initialHash);
  logger.debug('[Router Setup] Sempre redirecionando para /splash ao entrar no app');
  window.location.hash = ROUTES.SPLASH;
}
